import { useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Clock, Copy, KeyRound, Terminal } from 'lucide-react';
import { AlertBanner } from '@/components/AlertBanner';
import { Button } from '@/components/ui/button';
import { labelClassName } from '@/components/ui/label';
import { campaignCommand } from '@/lib/campaigns';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import type { Timestamp } from 'firebase/firestore';

/** Bouton « copier » qui bascule sur « Copié » et signale l'échec sans casser la page. */
function CopyButton({ value, label, copiedLabel, successMessage }: {
  value: string; label: string; copiedLabel: string; successMessage: string;
}) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(successMessage);
    } catch {
      toast.error(t('pairing.code.copy_failed'));
    }
  };
  return (
    <Button variant="secondary" size="sm" onClick={copy} aria-label={label}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? copiedLabel : label}
    </Button>
  );
}

/**
 * Le code de la campagne, mis en évidence et copiable.
 *
 * Il n'arrive QUE du retour de `createCampaign` : Firestore n'en garde qu'une
 * empreinte, aucun rechargement de page ne peut le retrouver — d'où
 * l'avertissement « affiché une seule fois », affiché avec lui.
 */
export function CampaignCodeCard({ code, expiresAt }: { code: string; expiresAt?: Timestamp | string | null }) {
  const { t, locale } = useT();
  return (
    <div
      data-testid="campaign-code"
      className="rounded-xl border border-brand-200 bg-brand-50/60 p-4 text-center dark:border-brand-900/40 dark:bg-brand-950/20"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-brand-700 dark:text-brand-300">
        {t('campaign.code.title')}
      </div>
      <code
        className="mt-2 block select-all break-all font-mono text-2xl font-semibold tracking-widest text-ink-900 dark:text-white sm:text-3xl"
        aria-label={t('campaign.code.title')}
      >
        {code}
      </code>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <CopyButton
          value={code}
          label={t('pairing.code.copy')}
          copiedLabel={t('pairing.code.copied_short')}
          successMessage={t('pairing.code.copied')}
        />
      </div>
      {expiresAt && (
        <p className="mt-2 text-[0.72rem] text-ink-500">
          <Clock className="mr-0.5 inline h-3 w-3" /> {t('pairing.code.expires', { when: fmtDate(expiresAt, locale) })}
        </p>
      )}
      <p className="mt-2 text-xs text-brand-800 dark:text-brand-200">{t('campaign.code.once')}</p>
    </div>
  );
}

/**
 * Ce qu'on affiche quand la session ne connaît plus le code (page rouverte
 * plus tard) : le portail ne peut PAS le relire, il le dit au lieu de laisser
 * croire à une erreur.
 */
export function CampaignCodeLost({ expiresAt }: { expiresAt?: Timestamp | string | null }) {
  const { t, locale } = useT();
  return (
    <AlertBanner variant="warning" icon={<KeyRound className="h-4 w-4" />} title={t('campaign.code.lost.title')}>
      <div>{t('campaign.code.lost.desc')}</div>
      {expiresAt && (
        <div className="mt-1 text-xs">
          <Clock className="mr-0.5 inline h-3 w-3" /> {t('campaign.col.expires')} {fmtDate(expiresAt, locale)}
        </div>
      )}
    </AlertBanner>
  );
}

/** La commande à lancer sur CHAQUE machine : le même code partout. */
export function CampaignCommand({ code }: { code?: string | null }) {
  const { t } = useT();
  const command = campaignCommand(code);
  return (
    <div>
      <div className={labelClassName()}>
        <Terminal className="mr-1 inline h-3 w-3" /> {t('campaign.command.title')}
      </div>
      <p className="mt-1 text-xs text-ink-500">{t('campaign.command.desc')}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code
          data-testid="campaign-command"
          className="min-w-0 flex-1 break-all rounded-lg bg-slate-100 px-2 py-1.5 text-[0.8rem] text-ink-800 dark:bg-ink-700 dark:text-ink-100"
        >
          {command}
        </code>
        <CopyButton
          value={command}
          label={t('campaign.command.copy')}
          copiedLabel={t('pairing.code.copied_short')}
          successMessage={t('campaign.command.copied')}
        />
      </div>
    </div>
  );
}
